import { useState, useCallback } from "react";

import { StickyNote, Check } from "lucide-react";
import { toast } from "sonner";

import { cn } from "@/modern/utils";
import { Button } from "@/modern/components/ui/button-shadcn";
import { updateNotes } from "@/network/network";
import { getSavedBoardSelection } from "@/react/hooks/useBoards";
import { WidgetParamKeys } from "@/types/entity";

import { useFileDetailModal } from "../../../context/FileDetailModalContext";

interface SummaryExtractionResultsProps {
  summary: string;
}

export const SummaryExtractionResults = ({ summary }: SummaryExtractionResultsProps) => {
  const { project, entityRecord, file, copyEntityLink } = useFileDetailModal();

  const [editedSummary, setEditedSummary] = useState(summary);
  const [created, setCreated] = useState(false);
  const [processing, setProcessing] = useState(false);

  const handleCreateNote = useCallback(async () => {
    if (!project || !entityRecord || created || processing) return;

    setProcessing(true);

    try {
      const boardNotesStore = project.boardNotesStore;
      if (!boardNotesStore) {
        throw new Error("Board notes store not available");
      }

      const fileBaseName = file?.name?.replace(/\.[^/.]+$/, "") || "";
      const noteName = fileBaseName ? `${fileBaseName} — Summary` : "Extracted Summary";

      const noteRecord = boardNotesStore.createRecord({
        [entityRecord.entityType]: entityRecord.id,
        name: noteName,
        description: editedSummary,
      });

      const selectedBoard = getSavedBoardSelection();
      const boardId = selectedBoard?.id ? Number(selectedBoard.id) : null;

      await new Promise<void>((resolve, reject) => {
        const detacher = boardNotesStore.on({
          async idChange({ record }: { record: any }) {
            if (record.id === noteRecord.id) {
              try {
                const paragraphs = editedSummary
                  .split(/\n\n+/)
                  .map((p) => p.trim())
                  .filter(Boolean)
                  .map((p) => `<p>${p.replace(/\n/g, "<br>")}</p>`)
                  .join("");
                let sourceTag = "";
                if (file?.id) {
                  const fileLink = await copyEntityLink(
                    WidgetParamKeys.FILE.EDIT,
                    file.id,
                    undefined,
                    true
                  );
                  const fileName = file.name || "document";
                  sourceTag = `<p><br></p><p><em>Extracted from: <a href="${fileLink}" target="_blank" rel="noopener noreferrer">${fileName}</a></em></p>`;
                }
                const htmlContent = `<h1>${noteName}</h1>${paragraphs}${sourceTag}`;

                await updateNotes(noteRecord.id, {
                  content: htmlContent,
                  board: boardId,
                  archived: false,
                });
                resolve();
              } catch (err) {
                reject(err);
              } finally {
                detacher();
              }
            }
          },
          once: true,
        });

        boardNotesStore.add(noteRecord);
      });

      setCreated(true);
      setProcessing(false);
      toast.success("Note Created", {
        description: "The extracted summary has been saved as a Swivu Note",
      });
    } catch (error) {
      console.error("[SummaryExtractionResults] Failed to create note:", error);
      setProcessing(false);
      toast.error("Failed to Create Note", {
        description: "Something went wrong. Please try again.",
      });
    }
  }, [project, entityRecord, editedSummary, created, processing, file, copyEntityLink]);

  return (
    <div className="flex flex-col gap-3 p-3">
      {/* Editable summary text */}
      <div className="border border-border rounded-swivu-base overflow-hidden">
        <textarea
          value={editedSummary}
          onChange={(e) => setEditedSummary(e.target.value)}
          disabled={created}
          rows={10}
          className={cn(
            "w-full p-3 text-sm text-foreground bg-transparent resize-none outline-none",
            "placeholder:text-muted-foreground",
            created && "opacity-60 cursor-not-allowed"
          )}
          placeholder="No summary content"
        />
      </div>

      {/* Create Note action */}
      <Button
        type="button"
        variant={created ? "ghost" : "default"}
        size="sm"
        disabled={created || processing || !editedSummary.trim()}
        onClick={handleCreateNote}
        className={cn(
          "w-full gap-2",
          created && "text-green-600 dark:text-green-400"
        )}
      >
        {created ? (
          <>
            <Check className="w-4 h-4" />
            Note Created
          </>
        ) : (
          <>
            <StickyNote className="w-4 h-4" />
            {processing ? "Creating..." : "Create Swivu Note"}
          </>
        )}
      </Button>
    </div>
  );
};
